/**
 * The controller hints (`03-client-app.md` section 3.4).
 *
 * On the brand board these sit in the bottom-right corner of the screen — `A Select  B Back` —
 * rather than taking a band off the bottom of the layout, so that is where they sit here. A is
 * lemon because it is the primary action; everything else is sand.
 *
 * It only appears when the last input was a gamepad, and it shows the actions of whatever is
 * focused right now, falling back to the buttons that always mean the same thing.
 */

import * as React from 'react';
import {
  currentControllerType,
  lastInputSource,
  onGamepad,
  onInputSourceChange,
  type GamepadButton,
  type InputSource,
} from '../../gamepad/input.js';
import { glyph, glyphSetFor, type GlyphSet } from '../../gamepad/glyphs.js';
import { useHints } from './Focusable.js';
import { useSettings } from '../../lib/settings.js';
import { useBigPicture } from '../../lib/bigPicture.js';

export interface Hint {
  button: GamepadButton;
  label: string;
}

export function HintsBar({ extra = [] }: { extra?: Hint[] }) {
  const hints = useHints();
  const { glyphOverride } = useSettings();
  const [source, setSource] = React.useState<InputSource>(lastInputSource);
  const [controller, setController] = React.useState(currentControllerType);
  const bigPicture = useBigPicture();

  React.useEffect(() => onInputSourceChange(setSource), []);
  React.useEffect(() => onGamepad((e) => setController(e.controller)), []);

  // On a desk the bar is a hint: it appears when a controller is picked up and goes away when
  // it is put down, because a keyboard does not need telling. In Big Picture it is part of the
  // console — on a television it is the only place the buttons are written down at all, and a
  // footer that vanished because somebody nudged a mouse would be taking that away.
  if (source !== 'gamepad' && !bigPicture) return null;

  const set: GlyphSet = glyphSetFor(controller, glyphOverride);

  // The actions of whatever is focused, and the two that always mean the same thing. Y and
  // Start are bound everywhere but not listed: a bar that names every button the console has is
  // a bar nobody reads, and the board draws two.
  const items: Hint[] = [
    { button: 'a', label: hints?.selectLabel ?? 'Select' },
    { button: 'b', label: 'Back' },
    ...(hints?.secondaryLabel ? [{ button: 'x' as const, label: hints.secondaryLabel }] : []),
    ...extra,
  ];

  return (
    <footer className="gb-hints" role="toolbar" aria-label={'Controller actions'}>
      {items.map((hint) => (
        <span
          key={`${hint.button}-${hint.label}`}
          className={`gb-hints__item${hint.button === 'a' ? ' gb-hints__item--primary' : ''}`}
        >
          <kbd className="gb-hints__glyph" aria-hidden="true">
            {glyph(hint.button, set)}
          </kbd>
          {hint.label}
        </span>
      ))}
    </footer>
  );
}
